import * as React from "react";
import ContentEditable from 'react-contenteditable';

import { ConceptResult, Response } from '../../../Shared/quill-marking-logic/src/main';
import {
  Cues,
  ALLOWED_ATTEMPTS,
  CORRECT_MATCHED,
  ENGLISH,
  FinalAttemptFeedback,
  Feedback,
  INSTRUCTIONS,
  ProgressBar,
  REVISE_MATCHED,
  TeacherPreviewMenuButton,
  getLatestAttempt,
  getParameterByName,
  hashToCollection,
  renderExplanation,
  hasTeacherPreview
} from '../../../Shared/index';
import { ReadAloudContent } from '../../../Shared/components/readAloud/readAloudContent';
import { ReadAloudSource } from '../../../Shared/components/readAloud/readAloudSource';
import { CLIP_SOURCE_TYPE, clipSource } from '../../../Shared/libs/speech/clipSource';
import { GRAMMAR } from '../../../Shared/utils/constants';
import { cancelSpeech } from '../../../Shared/libs/speech/speechManager';
import { ReadAloudHtml } from '../../../Shared/components/readAloud/readAloudHtml';
import { extractResponse } from '../../helpers/extractResponse';
import * as responseActions from '../../actions/responses';
import { setCurrentQuestion } from '../../actions/session';
import { GrammarActivity } from '../../interfaces/grammarActivities';
import { Question, ResponseAttempt } from '../../interfaces/questions';

const UNANSWERED = 'unanswered'
const CORRECTLY_ANSWERED = 'correctly answered'
const FINAL_ATTEMPT = 'final attempt'
const INCORRECTLY_ANSWERED = 'incorrectly answered'

interface QuestionProps {
  activity: GrammarActivity | null;
  allowPaste?: boolean;
  answeredQuestions: Question[] | never;
  availableLanguages?: string[];
  dispatch: Function;
  unansweredQuestions: Question[] | never;
  question: Question;
  goToNextQuestion: Function;
  checkAnswer: Function;
  conceptsFeedback: any;
  concepts: any;
  previewMode: boolean;
  questions: Question[];
  questionSet: Question[];
  handleToggleQuestion: (question: Question) => void;
  isOnMobile: boolean;
  handleTogglePreviewMenu: () => void;
  language?: string;
  translate: (language: string) => string;
  showTranslation: boolean;
  translatedQuestions?: any;
  languageLocale?: string;
}

interface QuestionState {
  showExample: boolean;
  response: string;
  questionStatus: string;
  submittedEmptyString: boolean;
  submittedForPreview: boolean;
  submittedSameResponseTwice: boolean;
  responses: { [key: number]: Response };
  previewAttempt: any;
}

export class QuestionComponent extends React.Component<QuestionProps, QuestionState> {
  constructor(props: QuestionProps) {
    super(props);

    // When a session is resumed mid-question, pre-fill the answer field with
    // the student's latest submitted response, mirroring Connect's
    // PlayLessonQuestion. A fresh question has no attempts and starts empty.
    const latestAttempt = this.handleGetLatestAttempt(props.question && props.question.attempts)

    this.state = {
      showExample: true,
      response: latestAttempt && latestAttempt.text ? latestAttempt.text : '',
      questionStatus: this.getCurrentQuestionStatus(props.question),
      submittedEmptyString: false,
      submittedForPreview: false,
      submittedSameResponseTwice: false,
      responses: {},
      previewAttempt: null
    }
  }

  componentDidMount() {
    const { allowPaste, question, } = this.props;
    // preview questions use key as the unique identifier
    const uid = question.uid ? question.uid : question.key;

    responseActions.getGradedResponsesWithCallback(
      uid,
      (data) => {
        this.setState({ responses: data, });
      }
    );

    if (!allowPaste) {
      window.addEventListener('paste', this.preventPaste, true);
    }
  }

  componentDidUpdate(prevProps: QuestionProps) {
    const { question, previewMode } = this.props;
    const { questionStatus } = this.state;
    const newQuestionStatus = this.getCurrentQuestionStatus(question)
    if (question && question.attempts && question.attempts.length > 0 && questionStatus !== newQuestionStatus) {
      this.setState({ questionStatus: newQuestionStatus })
    }
    if (question.uid && prevProps.question.uid !== question.uid) {
      responseActions.getGradedResponsesWithCallback(
        question.uid,
        (data: Response[]) => {
          this.setState({ responses: data, });
        }
      );
      // previewQuestion has been switched, reset values or set text to latest attempt
      if(previewMode) {
        const latestAttempt = this.handleGetLatestAttempt(question.attempts)
        if(latestAttempt && latestAttempt.text) {
          this.setState({ response: latestAttempt.text });
        } else {
          this.setState({ response: '' });
        }
        this.setState({ questionStatus: this.getCurrentQuestionStatus(question) });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('paste', this.preventPaste, true);
  }

  preventPaste = (e: ClipboardEvent) => {
    e.preventDefault()
    return false
  }

  getPreviewQuestionKeys = () => {
    const { activity, questionSet } = this.props;
    let questionKeys;
    if(activity.questions && activity.questions.length) {
      questionKeys = activity.questions.map(question => question.key);
    } else {
      questionKeys = questionSet.map(question => question.uid);
    }
    return questionKeys;
  }

  getPreviewQuestionIndex = (question, questions) => {
    return questions.indexOf(question.uid ? question.uid : question.key);
  }

  previousResponses = () => {
    const { question } = this.props
    return question.attempts ? question.attempts.map(a => extractResponse(a).text) : []
  }

  getCurrentQuestionStatus(question) {
    if (question.attempts && question.attempts.length) {
      const lastAttempt = extractResponse(question.attempts[question.attempts.length - 1]);
      if (question.attempts.length === ALLOWED_ATTEMPTS && lastAttempt) {
        if (lastAttempt.optimal) {
          return CORRECTLY_ANSWERED
        } else {
          return FINAL_ATTEMPT
        }
      } else {
        if (question.attempts.length < ALLOWED_ATTEMPTS && lastAttempt && lastAttempt.optimal) {
          return CORRECTLY_ANSWERED
        } else {
          return INCORRECTLY_ANSWERED
        }
      }
    } else {
      return UNANSWERED
    }
  }

  correctResponse = () => {
    const { responses } = this.state
    const { question } = this.props
    let text
    if (Object.keys(responses).length) {
      const responseArray = hashToCollection(responses).sort((a: Response, b: Response) => b.count - a.count)
      const correctResponse = responseArray.find((r: Response) => r.optimal)
      if (correctResponse) {
        text = correctResponse.text
      }
    }
    if (!text) {
      text = question.answers[0].text.replace(/{|}/gm, '')
    }
    return text
  }

  handleCheckWorkClick = () => {
    const { checkAnswer, question } = this.props
    const { response, responses } = this.state
    const isFirstAttempt = !question.attempts || question.attempts.length === 0
    if (Object.keys(responses).length) {
      if (response !== '') {
        const trimmedResponse = response.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, ' ')
        const firstAttemptResponse = isFirstAttempt ? null : extractResponse(question.attempts[0]).text;
        if (!isFirstAttempt && response === firstAttemptResponse) {
          this.setState({ submittedSameResponseTwice: true })
        } else {
          checkAnswer(trimmedResponse, question, responses, isFirstAttempt)
          this.setState({ submittedEmptyString: false, submittedSameResponseTwice: false })
        }
      } else {
        this.setState({ submittedEmptyString: true })
      }
    }
  }

  handleNextProblemClick = () => {
    const { dispatch, goToNextQuestion, previewMode, unansweredQuestions, handleToggleQuestion } = this.props;
    cancelSpeech();
    if(previewMode) {
      const nextQuestion = unansweredQuestions[0];
      const action = setCurrentQuestion(nextQuestion);
      dispatch(action);
      handleToggleQuestion(nextQuestion);
    } else {
      goToNextQuestion();
      this.setState({ response: '', questionStatus: UNANSWERED, responses: {} })
    }
    const element = document.getElementById("main-content")
    if (!element) { return }
    element.focus()
  }

  handleExampleButtonClick = () => this.setState(prevState => ({ showExample: !prevState.showExample }))

  handleResponseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ response: e.target.value })
  }

  getNegativeConceptResultsForResponse(conceptResults: ConceptResult[]) {
    return hashToCollection(conceptResults).filter((cr: ConceptResult) => !cr.correct);
  }

  getNegativeConceptResultForResponse(conceptResults: ConceptResult[]) {
    const negCRs = this.getNegativeConceptResultsForResponse(conceptResults);
    return negCRs.length > 0 ? negCRs[0] : undefined;
  }

  handleGetLatestAttempt(attempts: ResponseAttempt[] = []): Response | undefined {
    return extractResponse(getLatestAttempt(attempts));
  }

  getConcept = () => {
    const { concepts, question } = this.props
    return concepts && concepts.data && concepts.data[0] ? concepts.data[0].find((c: any) => c.uid === question.concept_uid) : null
  }

  handleKeyDown = (e: any) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      e.preventDefault();
      const { questionStatus } = this.state
      if (questionStatus === UNANSWERED || questionStatus === INCORRECTLY_ANSWERED) {
        this.handleCheckWorkClick()
      } else {
        this.handleNextProblemClick()
      }
    }
  }

  handleDrop = (e) => e.preventDefault()

  example = (): JSX.Element | string | void => {
    const { question } = this.props
    if (question.rule_description && question.rule_description.length && question.rule_description !== "<br/>") {
      return question.rule_description
    } else if (this.getConcept() && this.getConcept().description) {
      return this.getConcept().description
    }
  }

  renderExample(): JSX.Element | undefined {
    const { showExample, } = this.state
    const example = this.example()
    if (example) {
      let componentClasses = 'example-container flex-start'
      if (showExample) {
        componentClasses += ' show'
      }
      return (
        <div className={componentClasses}>
          <ReadAloudHtml className="example" html={example.replace(/\n/g, "<br />")} label="Read the example aloud" />
        </div>
      );

    } else {
      return undefined
    }
  }

  renderCheckAnswerButton(): JSX.Element | void {
    const { questionStatus, responses, response } = this.state
    const { unansweredQuestions, previewMode, language, availableLanguages, translate, languageLocale } = this.props;
    let showTranslatedButtonText = false
    let defaultButtonText = 'Get feedback'
    let lang
    if (language && language !== ENGLISH && availableLanguages?.includes(language)) {
      showTranslatedButtonText = true
      defaultButtonText = translate(`buttons^${defaultButtonText.toLowerCase()}`)
      lang = languageLocale
    }
    const buttonClassName = "quill-button contained large green focus-on-light"

    if (!Object.keys(responses).length) { return }

    if ([CORRECTLY_ANSWERED, FINAL_ATTEMPT].includes(questionStatus)) {
      let buttonText = unansweredQuestions.length === 0 ? 'Next' : 'Next question';
      if(showTranslatedButtonText) {
        buttonText = translate(`buttons^${buttonText.toLowerCase()}`)
      }
      const disabledStatus = previewMode && buttonText === 'Next' ? 'disabled' : '';
      return <button className={`${buttonClassName} ${disabledStatus}`} disabled={!!disabledStatus} lang={lang} onClick={this.handleNextProblemClick} type="submit">{buttonText}</button>
    }
    if (!response.length || this.previousResponses().includes(response)) {
      return <button className={`${buttonClassName} disabled`} lang={lang} type="submit">{defaultButtonText}</button>
    }
    return <button className={buttonClassName} lang={lang} onClick={this.handleCheckWorkClick} type="submit">{defaultButtonText}</button>
  }

  getQuestionCounts = (): object => {
    const { answeredQuestions, unansweredQuestions, activity, previewMode, questionSet, question } = this.props;
    let answeredQuestionCount;
    let totalQuestionCount;
    if(previewMode) {
      // standard activity questions
      // some Grammar activities return an empty array for the questions property so we check it's length
      if(activity && activity.questions && activity.questions.length) {
        const questions = activity.questions ? activity.questions.map(question => question.key) : [];
        const index = questions && this.getPreviewQuestionIndex(question, questions);
        answeredQuestionCount = index === -1 ? 1 : index + 1;
        totalQuestionCount = questions.length;
      } else if(questionSet) {
        const questions = this.getPreviewQuestionKeys();
        const index = questions.indexOf(question.uid ? question.uid : question.key);
        answeredQuestionCount = index === -1 ? 1 : index + 1;
        totalQuestionCount = questions.length;
      }
    } else {
      answeredQuestionCount = answeredQuestions.length + 1;
      totalQuestionCount = answeredQuestionCount + unansweredQuestions.length;
    }
    return {
      answeredQuestionCount,
      totalQuestionCount
    };
  }

  renderTopSection(): JSX.Element {
    const { activity, isOnMobile, handleTogglePreviewMenu } = this.props;
    const counts = this.getQuestionCounts();
    const meterWidth = counts['answeredQuestionCount'] / counts['totalQuestionCount'] * 100
    const studentSession = getParameterByName('student', window.location.href);
    return (
      <div className="top-section">
        {isOnMobile && (!studentSession || hasTeacherPreview()) && <TeacherPreviewMenuButton containerClass="is-on-mobile" onTogglePreview={handleTogglePreviewMenu} />}
        <ProgressBar
          answeredQuestionCount={counts['answeredQuestionCount']}
          label="questions"
          percent={meterWidth}
          questionCount={counts['totalQuestionCount']}
        />
        <div className="flex-space-between">
          <ReadAloudContent label="Read the title aloud"><h1>{activity ? activity.title : null}</h1></ReadAloudContent>
        </div>
        {this.renderExample()}
      </div>
    )
  }

  renderTextareaSection = () => {
    const { questionStatus, response } = this.state;
    const noMoreSubmissionsForStudentSession = [CORRECTLY_ANSWERED, FINAL_ATTEMPT].includes(questionStatus)
    const disabled = noMoreSubmissionsForStudentSession ? 'disabled' : '';

    return (
      <div className={`student text-editor card is-fullwidth ${disabled ? 'disabled-editor' : ''}`}>
        <div className="card-content">
          <div className="content">
            <ContentEditable
              className="connect-text-area"
              data-gramm={false}
              disabled={!!disabled}
              html={response}
              onChange={this.handleResponseChange}
              onDrop={this.handleDrop}
              onKeyDown={this.handleKeyDown}
              placeholder="Type your answer here."
              spellCheck={false}
            />
          </div>
        </div>
      </div>
    )
  }

  renderQuestionSection(): JSX.Element {
    const { question } = this.props
    const { prompt, attempts, cues_label, } = question
    const latestAttempt = this.handleGetLatestAttempt(attempts)
    const feedbackKey = latestAttempt ? latestAttempt.text : 'instructions'
    return (
      <div className="student-container">
        <ReadAloudHtml className="sentence-fragments" html={prompt} label="Read the sentence aloud" />
        <Cues displayArrowAndText={true} includeReadAloudButton={true} question={{...question, cuesLabel: cues_label, }} />
        {this.renderFeedbackSection()}
        {this.renderTextareaSection()}
        <div className="question-button-group">
          {this.renderCheckAnswerButton()}
        </div>
        {this.renderConceptExplanation()}
      </div>
    )
  }

  renderFeedbackSection(): JSX.Element | undefined {
    const { showTranslation, translate, question, languageLocale } = this.props
    const latestAttempt: Response | undefined = this.handleGetLatestAttempt(question.attempts)
    // this is how Connect latestAttempts are structured so we need to match for this shared Feedback component
    const attemptToPass = { response: latestAttempt }
    if (!latestAttempt) {
      return(
        <Feedback
          feedbackHtml={question.instructions}
          feedbackType={INSTRUCTIONS}
          languageLocale={languageLocale}
          question={question}
          showTranslation={showTranslation}
        />
      )
    }
    if (latestAttempt && latestAttempt.optimal) {
      return(
        <Feedback
          feedbackHtml={latestAttempt.feedback}
          feedbackType={CORRECT_MATCHED}
          languageLocale={languageLocale}
          latestAttempt={attemptToPass}
          question={question}
          showTranslation={showTranslation}
        />
      )
    }
    if (question.attempts && question.attempts.length === ALLOWED_ATTEMPTS) {
      return(
        <FinalAttemptFeedback
          correctResponse={this.correctResponse()}
          languageLocale={languageLocale}
          latestAttempt={latestAttempt?.text}
          showTranslation={showTranslation}
          translate={translate}
        />
      )
    }
    return(
      <Feedback
        feedbackHtml={latestAttempt?.feedback ? latestAttempt.feedback : ''}
        feedbackType={REVISE_MATCHED}
        languageLocale={languageLocale}
        latestAttempt={attemptToPass}
        question={question}
        showTranslation={showTranslation}
        translate={translate}
      />
    )
  }


  renderConceptExplanation = (): JSX.Element | void => {
    const { conceptsFeedback, question, showTranslation, languageLocale } = this.props
    const explanationFor = (key: string) => renderExplanation({ activityType: GRAMMAR, conceptsFeedback, data: conceptsFeedback.data[key], key, languageLocale, showTranslation, });
    const latestAttempt: Response | undefined = this.handleGetLatestAttempt(question.attempts);
    if(!latestAttempt || latestAttempt.optimal) { return }
    if (latestAttempt.conceptResults) {
      const conceptID = this.getNegativeConceptResultForResponse(latestAttempt.conceptResults);
      if (conceptID) {
        const key = conceptID.conceptUID
        return explanationFor(key);
      }
      // pretty sure it is only conceptResults now, but trying to avoid further issues
    } else if (latestAttempt.concept_results) {
      const conceptID = this.getNegativeConceptResultForResponse(latestAttempt.concept_results);
      if (conceptID) {
        const key = conceptID.conceptUID
        return explanationFor(key);
      }

    } else if (question && question.modelConceptUID) {
      const key = question.modelConceptUID
      return explanationFor(key);
    } else if (question.concept_uid) {
      const key = question.concept_uid
      return explanationFor(key);
    }
  }

  questionSource = () => {
    const { question, } = this.props
    return clipSource(CLIP_SOURCE_TYPE.QUESTION, question.uid || question.key)
  }

  render(): JSX.Element {
    return (
      <ReadAloudSource source={this.questionSource()}>
        <div className="student-container-inner-diagnostic">
          {this.renderTopSection()}
          {this.renderQuestionSection()}
        </div>
      </ReadAloudSource>
    )
  }
}

export default QuestionComponent;
